import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowDownUp, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatCard } from "@/components/common";
import { MovementDialog } from "@/components/stock/MovementDialog";
import { api, brl, fmtQty } from "@/lib/api";
import { findByCode, isCode, useScanner } from "@/lib/barcode";

export function StockPosition({ canChange }) {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [onlyLow, setOnlyLow] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const load = useCallback(() => Promise.all([api.get("/stock/items"), api.get("/suppliers")]).then(([i, s]) => { setItems(i.data); setSuppliers(s.data); }), []);
  useEffect(() => { load(); }, [load]);
  const list = items.filter((i) => (!onlyLow || i.low) && (i.name.toLowerCase().includes(q.toLowerCase()) || (q.trim() && (i.barcode || "").includes(q.trim()))));
  const box = useRef(null);
  const scanOpen = (c) => {
    const i = findByCode(items.filter((x) => x.kind === "product"), c);
    if (i) { setSel({ ...i, type: "entrada", scan: true }); setQ(""); }
    else toast.error(`Nenhum produto com controle de estoque tem o código ${c}. Ative o controle de estoque no cadastro do produto.`);
  };
  useScanner(scanOpen, box, canChange);
  return (
    <div ref={box} className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Itens controlados" value={items.length} testId="stock-stat-items" />
        <StatCard label="Estoque baixo" value={items.filter((i) => i.low).length} tone="danger" icon={AlertTriangle} testId="stock-stat-low" />
        <StatCard label="Insumos" value={items.filter((i) => i.kind === "ingredient").length} tone="teal" testId="stock-stat-ingredients" />
        <StatCard label="Valor em estoque" value={brl(items.reduce((s, i) => s + i.value, 0))} tone="success" testId="stock-stat-value" />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={canChange ? "Buscar item ou passar o leitor para dar entrada" : "Buscar item"} className="pl-9"
          onKeyDown={(e) => { if (e.key === "Enter" && canChange && isCode(q)) scanOpen(q.trim()); }} data-testid="stock-search-input" /></div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={onlyLow} onCheckedChange={setOnlyLow} data-testid="stock-only-low-switch" />Somente estoque baixo</label>
      </div>
      <div className="card-surface overflow-x-auto">
        <Table data-testid="stock-position-table">
          <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Tipo</TableHead><TableHead className="text-right">Saldo</TableHead><TableHead className="text-right">Mínimo</TableHead><TableHead className="text-right">Custo médio</TableHead><TableHead className="text-right">Valor</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {list.map((i) => (
              <TableRow key={i.id} data-testid={`stock-row-${i.id}`}>
                <TableCell className="font-medium">{i.name} {i.low && <span className="ml-1 rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-red-500" data-testid={`stock-low-badge-${i.id}`}>BAIXO</span>}</TableCell>
                <TableCell className="text-muted-foreground">{i.kind === "product" ? "Produto" : "Insumo"}</TableCell>
                <TableCell className={`text-right font-mono ${i.stock < 0 ? "text-red-500" : ""}`}>{fmtQty(i.stock)} {i.unit}</TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">{fmtQty(i.min_stock)}</TableCell>
                <TableCell className="text-right font-mono">{brl(i.cost)}</TableCell>
                <TableCell className="text-right font-mono">{brl(i.value)}</TableCell>
                <TableCell className="text-right">{canChange && <Button size="sm" variant="outline" onClick={() => setSel(i)} data-testid={`stock-move-btn-${i.id}`}><ArrowDownUp className="h-4 w-4 mr-1" />Movimentar</Button>}</TableCell>
              </TableRow>
            ))}
            {!list.length && <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">Nenhum item. Ative o controle de estoque nos produtos ou cadastre insumos.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      {sel && <MovementDialog item={sel} suppliers={suppliers} onClose={() => setSel(null)} onDone={() => { setSel(null); load(); }} />}
    </div>
  );
}
